import React, { useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  Alert,
  APIActions,
  Box,
  Button,
  CustomDialog,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  SearchBar,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  Tooltip,
  Typography,
} from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { useDataTable } from 'web-library/stories/organism/DataTable/DataTable';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { MutationStatus } from 'react-query';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconButton from '@mui/material/IconButton';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import DeleteButton from '../../../components/buttons/DeleteButton';
import RouteKey from '../../../routes/key';
import { useDeleteListing } from '../../../api-refactored/group-special-consideration/useDeleteListing';
import {
  GroupSCListingResponse,
  PageDataContent,
  usePostGroupSCListing,
} from '../../../api-refactored/group-special-consideration/usePostGroupSCListing';
import SelectExamLevelCoordinatorModal from './SelectExamLevelCoordinatorModal';
import { ExamSeriesModel } from '../../../api-refactored/special-consideration/useGetExamSeries';

const { specialConsideration: specialConsiderationRoute } = RouteKey.child;
const { specialConsideration, instructions, coordinatorApplicationSummary } = RouteKey[
  'special-consideration'
].child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginBottom: theme.spacing(3),
    },
  },
  newApplicationButton: {
    height: 'fit-content',
  },
  tableSearchContainer: {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  textDisabled: {
    color: theme.color.text.textDisabled,
  },
  buttonContainer: {
    display: 'inline-flex',
  },
  button: {
    margin: 'auto',
    padding: 0,
    width: 'max-content',
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  paddingRight: {
    paddingRight: theme.spacing(6),
  },
  iconButtonContainer: {
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  iconRemove: {
    margin: 'auto',
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
}));

export enum AddCandidateMode {
  applyForNone = '',
  applyForIndividual = 'applyForIndividual',
  applyForGroup = 'applyForGroup',
}

enum ApplicationStatus {
  SUBMITTED = 'Submitted',
  DRAFT = 'Draft',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
}

const specialConsiderationAlert = (
  data: GroupSCListingResponse,
  classes: Record<string,string>,
  t: TFunction<'translation'>
) => {
  const { pageData, errorMsg, canApplication } = data;
  // Display no exam registration error
  if (errorMsg === 'You have not registered for any examination.' && !canApplication) {
    return (
      <Alert
        className={classes.alert}
        variant="filled"
        severity="error"
        message={t('specialConsideration->>Listing.NoRegisteredExam')}
      />
    );
  }
  // Display types of exam level closed error
  const unavailableExamLevels = getUnavailableExamLevels(pageData.content);

  if (unavailableExamLevels.length) {
    return (
      <Alert
        className={classes.alert}
        variant="filled"
        severity="error"
        message={t('specialConsideration->>Listing.Unavailable', {
          examLevels: unavailableExamLevels,
        })}
      />
    );
  }
  // Don't display any error if there is no draft
  return <></>;
};

const getUnavailableExamLevels = (specialConsiderations: PageDataContent[]) => {
  const unavailableExamLevels: string[] = [];

  specialConsiderations.forEach((specialConsiderationItem) => {
    if (
      specialConsiderationItem.applcationStatus === ApplicationStatus.DRAFT &&
      specialConsiderationItem.applicationClosed &&
      unavailableExamLevels.every(
        (unavailableExamLevel) => unavailableExamLevel !== specialConsiderationItem.examLevel
      )
    ) {
      unavailableExamLevels.push(specialConsiderationItem.examLevel);
    }
  });
  unavailableExamLevels.sort((a, b) => a.localeCompare(b));

  return unavailableExamLevels.join(', ');
};

const useTableColumns = (
  tableState: TableState,
  handleOpenDeleteModal: (uuid: string) => void,
  t: TFunction,
  history: RouteComponentProps['history'],
  classes: Record<string,string>,
  theme: AugmentedTheme
): MUIDataTableColumnDef[] => [
    {
      name: 'modeOfAddingCandidate',
      label: t('specialConsideration->>Listing.ModeOfAddingCandidate'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '35px' } }),
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex].groupScType}</Typography>
        ),
      },
    },
    {
      name: 'nricFin',
      label: t('specialConsideration->>Listing.NricFin'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '35px' } }),
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex].candidateId ?? '-'}</Typography>
        ),
      },
    },
    {
      name: 'candidateName',
      label: t('specialConsideration->>Listing.CandidateName'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '35px' } }),
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex].candidateName ?? '-'}</Typography>
        ),
      },
    },
    {
      name: 'examLevel',
      label: t('specialConsideration->>Listing.ExamLevel'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '20%', paddingTop: '35px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{tableState.data[dataIndex].examLevel}</Typography>
        ),
      },
    },
    {
      name: 'applicationDateTime',
      label: t('specialConsideration->>Listing.ApplicationDateTime'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '35px' } }),
        sort: true,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">
            {stringToLongDateAndShortTime(tableState.data[dataIndex].applicationDate)}
          </Typography>
        ),
      },
    },
    {
      name: 'status',
      label: t('specialConsideration->>Listing.Status'),
      options: {
        setCellProps: () => ({ style: { paddingTop: '35px' } }),
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (tableState.data[dataIndex].applcationStatus) {
            const { applcationStatus } = tableState.data[dataIndex];

            const checkApplicationStatus = () => {
              if (applcationStatus === ApplicationStatus.SUBMITTED) {
                return 'info';
              }
              if (applcationStatus === ApplicationStatus.APPROVED) {
                return 'positive';
              }
              if (applcationStatus === ApplicationStatus.REJECTED) {
                return 'negative';
              }
              return 'info';
            };

            return (
              <>
                <Pill
                  variant={checkApplicationStatus()}
                  label={tableState.data[dataIndex].applcationStatus}
                />
                <Box marginTop={1}>
                  <Typography variant="body2">
                    Last Updated:{' '}
                    {stringToSemanticDisplay(tableState.data[dataIndex].lastUpdateDatetime)}
                  </Typography>
                </Box>
              </>
            );
          }
          return <></>;
        },
      },
    },
    {
      name: 'action',
      label: t('specialConsideration->>Listing.Action'),
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.buttonContainer}>
            {tableState.data[dataIndex].applcationStatus !== ApplicationStatus.DRAFT && (
              <>
                <Button
                  id={`button-specialConsideration-view-${dataIndex}`}
                  className={`${classes.button} ${classes.buttonFont}`}
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() =>
                    history.push(
                      `/${specialConsiderationRoute}/${specialConsideration.route}/${tableState.data[dataIndex].uuid}/${coordinatorApplicationSummary.route}`
                    )
                  }
                >
                  {t('specialConsideration->>Listing.View')}
                </Button>
              </>
            )}
            {tableState.data[dataIndex].applcationStatus === ApplicationStatus.DRAFT && (
              <Button
                id={`button-specialConsideration-continue-${dataIndex}`}
                className={`${classes.button} ${classes.paddingRight} ${classes.buttonFont}`}
                variant="text"
                color="primary"
                size="small"
                disabled={tableState.data[dataIndex].applicationClosed}
                onClick={() =>
                  history.push(
                    `/${specialConsiderationRoute}/${specialConsideration.route}/${instructions.route}`,
                    {
                      isContinueApplication: true,
                      applicationUuid: tableState.data[dataIndex].uuid,
                    }
                  )
                }
              >
                {t('specialConsideration->>Listing.Continue')}
              </Button>
            )}
            {tableState.data[dataIndex].applcationStatus === ApplicationStatus.DRAFT && (
              <DeleteButton
                buttonId={dataIndex}
                deleteHandler={() => handleOpenDeleteModal(tableState.data[dataIndex].uuid)}
                label={t('specialConsideration->>Listing.Delete')}
              />
            )}
            {tableState.data[dataIndex].amendmentDraft && (
              <Tooltip
                arrow
                placement="bottom"
                title={<>{t('accessArrangement->>AccessArrangements.AmendmentDraft')}</>}
              >
                <IconButton size="large">
                  <Icon name={IconName.alertOff} color={theme.color.icon.iconDefault} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    },
  ];

const SpecialConsiderationCoordinatorListingTable = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<string>('');

  const [dialogCustomStatus, setDialogCustomStatus] = useState<MutationStatus>('idle');

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [examLevel, setExamLevel] = useState<string | null>(null);
  const [examSeries, setExamSeries] = useState<string | null>(null);
  const [isError, setIsError] = useState<boolean>(false);

  const [examSeriesPSLEObject, setExamSeriesPSLEObject] = useState<ExamSeriesModel | undefined>(
    undefined
  );

  const [isPSLE, setIsPSLE] = useState<boolean>(false);

  const [addCandidateMode, setAddCandidateMode] = useState<AddCandidateMode>(
    AddCandidateMode.applyForNone
  );

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const onDialogClose = () => {
    setOpenModal(false);
    setExamLevel(null);
    setExamSeries(null);
    setIsError(false);
    setDialogCustomStatus('idle');
    setAddCandidateMode(AddCandidateMode.applyForNone);
    setExamSeriesPSLEObject(undefined);
    setIsPSLE(false);
  };

  const { state: tableState, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    size: 10,
  });

  const {
    data: listingData,
    mutate: mutatePostCoordinatorCandidateListing,
    isLoading,
  } = usePostGroupSCListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.pageData.content,
          count: data.pageData.totalElements,
        },
      } as TableActionTypes);
    },
  });

  const handleOpenDeleteModal = (uuid: string) => {
    setDeleteData(uuid);
    setOpenDeleteModal(true);
  };

  const tableColumns = useTableColumns(
    tableState,
    handleOpenDeleteModal,
    t,
    history,
    classes,
    theme
  );

  const deleteSpecialConsiderationRequest = useDeleteListing({
    onSuccess: () => {
      mutatePostCoordinatorCandidateListing({
        q: tableState.search,
        pathParams: {
          order: tableState.order,
          sort: tableState.sortColumn,
          page: tableState.page,
          pageSize: tableState.size,
        },
      });
    },
  });

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (deleteData) {
      deleteSpecialConsiderationRequest.mutate(deleteData);
    }
    handleCloseDeleteModal();
  };

  useEffect(() => {
    mutatePostCoordinatorCandidateListing({
      q: tableState.search,
      pathParams: {
        pageSize: tableState.size,
        page: tableState.page,
        order: tableState.order,
        sort: tableState.sortColumn,
      },
    });
  }, [
    tableState.search,
    tableState.order,
    tableState.sortColumn,
    tableState.page,
    tableState.size,
    mutatePostCoordinatorCandidateListing,
  ]);

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      pagination: true,
      sort: true,
    }),
    [tOptions]
  );

  return (
    <>
      <Box className={classes.headerContainer}>
        <SectionHeader
          title={t('specialConsideration->>Listing.SpecialConsiderationApplications')}
        />
        <Button
          id="button-new-application-sc"
          className={classes.newApplicationButton}
          fullWidth={isMobile}
          onClick={handleOpenModal}
          disabled={!listingData?.canApplication}
        >
          {t('specialConsideration->>Listing.NewApplication')}
        </Button>
      </Box>
      <Box>
        {listingData && specialConsiderationAlert(listingData, classes, t)}
        <SearchBar
          className={classes.searchContainer}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue=""
          placeholder={t('specialConsideration->>Listing.SearchByAffectCandidateNameAndId')}
          onSearch={(searchStr: string) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: searchStr,
            });
          }}
        />
        <MUIDataTable
          isLoading={isLoading}
          columns={tableColumns}
          data={tableState.data}
          dispatch={tableDispatch}
          state={tableState}
          options={customOptions}
          rowsPerPageOptions={[10, 20, 25, 30, 50]}
          emptyDataLabel={t('specialConsideration->>Listing.EmptyDataLabel')}
        />
        <CustomDialog
          open={openDeleteModal}
          title={t('specialConsideration->>Listing.DeleteTitle')}
          message={t('specialConsideration->>Listing.DeleteMessage')}
          action={
            <APIActions
              onClose={handleCloseDeleteModal}
              apiCall={handleDelete}
              cancelString={t('specialConsideration->>Listing.Cancel')}
              apiString={t('specialConsideration->>Listing.Proceed')}
            />
          }
        />
      </Box>
      <SelectExamLevelCoordinatorModal
        modalState={openModal}
        onDialogClose={onDialogClose}
        examLevel={examLevel}
        examSeries={examSeries}
        isError={isError}
        addCandidateMode={addCandidateMode}
        dialogCustomStatus={dialogCustomStatus}
        examSeriesPSLEObject={examSeriesPSLEObject}
        isPSLE={isPSLE}
        setExamLevel={setExamLevel}
        setExamSeries={setExamSeries}
        setIsError={setIsError}
        setAddCandidateMode={setAddCandidateMode}
        setDialogCustomStatus={setDialogCustomStatus}
        setExamSeriesPSLEObject={setExamSeriesPSLEObject}
        setIsPSLE={setIsPSLE}
      />
    </>
  );
};
export default SpecialConsiderationCoordinatorListingTable;
